import { Timestamp } from 'firebase/firestore';

export type DistanceUnit = 'km' | 'mi';

export interface User {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  province: string;
  fullLegalName?: string; // kept for filings
  businessNumber?: string; // 9 digits BN
  gstHstAccount?: string; // 123456789RT0001
  gstRegistered: boolean;
  distanceUnit?: DistanceUnit; // default "km"
  preferredTaxPeriod: {
    startDate: Date;
    endDate: Date;
  };
  billing?: {
    plan: string;
    status: string;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    currentPeriodStart?: Timestamp;
    currentPeriodEnd?: Timestamp;
    archiveEndsAt?: Timestamp;
    pendingDowngrade?: boolean;
    hasArchive?: boolean;
    hasPro?: boolean;
  };
  storageUsage?: {
    totalBytes: number;
    receiptCount: number;
    lastUpdated: Timestamp;
  };
  flags?: UserFlags; // abuse detection and account status flags
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface UserFlags {
  onHold?: boolean; // account is read-only due to abuse detection
  reason?: 'excessive_upload' | 'manual_review' | 'payment_issue';
  at?: Timestamp; // when flag was set
  threshold?: {
    bytes?: number;
    count?: number;
    thresholdBytes?: number;
    thresholdCount?: number;
  };
}

export interface MealContext {
  type?: 'TRAVEL' | 'ENTERTAIN';
  attendees?: string;
  overnight?: boolean;
  note?: string;
  // New fields for narrow exception validation (Oct 24, 2025)
  distanceMiles?: number;
  hotelReceiptUrl?: string;
  meetingDetails?: string;
  meetingDateTime?: string;
}

export interface Expense {
  id: string;
  userId: string;
  date: Date;
  category: ExpenseCategory;
  subCategory: string;
  amount: number;
  gstPaid: number;
  businessUsePct: number;
  claimableITC: number;
  description: string;
  receiptPhotoURL?: string;
  uploadedAt: Date; // Changed from Timestamp to Date for Redux serialization
  source: 'manual' | 'import';
  // NEW — meals/entertain compliance
  qualifies50Pct?: boolean; // default false; true only if overnight travel OR documented client entertainment
  mealContext?: MealContext;
}

export interface Session {
  id: string;
  userId: string;
  startOdometer: number;
  endOdometer: number;
  totalKmDriven: number;
  totalKmWithPassenger: number;
  grossEarnings: number;
  gstCollected: number;
  expensesLinked: string[];
  startTime: Timestamp;
  endTime: Timestamp;
  drivingDurationMinutes?: number; // User-adjustable driving duration in minutes
}

export interface Report {
  id: string;
  userId: string;
  period: {
    start: Date;
    end: Date;
  };
  gstLines: {
    line90: number; // Total taxable sales
    line91: number; // Exempt supplies
    line101: number; // Total sales and other revenue
    line103: number; // GST collected
    line106: number; // ITCs
    line108: number; // Adjustments
    line109: number; // Net Tax
    line115: number; // Amount owing/refund
  };
  totalIncome: number;
  totalExpenses: number;
  totalITCs: number;
  totalGSTCollected: number;
  netGST: number;
  netIncome: number;
  fileURL?: string;
  createdAt: Timestamp;
}

export type ExpenseCategory =
  | 'fuel'
  | 'maintenance'
  | 'vehicle-upkeep'
  | 'insurance'
  | 'phone-data'
  | 'accessories'
  | 'clothing'
  | 'meals'
  | 'entertainment'
  | 'miscellaneous'
  | 'lease-payment'
  | 'loan-interest'
  | 'tax-services'
  | 'custom';

export interface ExpenseTag {
  category: ExpenseCategory;
  name: string;
  gstClaimable: boolean;
  description?: string;
}

export const EXPENSE_TAGS: Record<ExpenseCategory, ExpenseTag[]> = {
  fuel: [
    { category: 'fuel', name: 'Chevron', gstClaimable: true },
    {
      category: 'fuel',
      name: 'EV Charging Equipment',
      gstClaimable: true,
      description: 'Level 2 charger, adapters, etc.',
    },
    { category: 'fuel', name: 'Esso', gstClaimable: true },
    {
      category: 'fuel',
      name: 'Home Charging',
      gstClaimable: true,
      description: 'In-house EV charging costs',
    },
    { category: 'fuel', name: 'Husky', gstClaimable: true },
    { category: 'fuel', name: 'Other Gas Station', gstClaimable: true },
    { category: 'fuel', name: 'Petro-Canada', gstClaimable: true },
    {
      category: 'fuel',
      name: 'Public EV Charging',
      gstClaimable: true,
      description: 'Third-party EV charging stations',
    },
    { category: 'fuel', name: 'Shell', gstClaimable: true },
    {
      category: 'fuel',
      name: 'Supercharge (Tesla)',
      gstClaimable: true,
      description: 'Tesla Supercharger network',
    },
  ],
  maintenance: [
    { category: 'maintenance', name: 'Battery', gstClaimable: true },
    { category: 'maintenance', name: 'Brakes', gstClaimable: true },
    {
      category: 'maintenance',
      name: 'Commercial Inspection',
      gstClaimable: true,
      description: 'Annual commercial vehicle inspection',
    },
    { category: 'maintenance', name: 'Filters', gstClaimable: true },
    { category: 'maintenance', name: 'Fluids', gstClaimable: true },
    { category: 'maintenance', name: 'Oil Change', gstClaimable: true },
    { category: 'maintenance', name: 'Tire Balancing', gstClaimable: true },
    { category: 'maintenance', name: 'Tires', gstClaimable: true },
    { category: 'maintenance', name: 'Wipers', gstClaimable: true },
  ],
  'vehicle-upkeep': [
    { category: 'vehicle-upkeep', name: 'Car Camera', gstClaimable: true },
    { category: 'vehicle-upkeep', name: 'Car Wash', gstClaimable: true },
    { category: 'vehicle-upkeep', name: 'Cleaning Supplies', gstClaimable: true },
    { category: 'vehicle-upkeep', name: 'Floor Mats', gstClaimable: true },
    { category: 'vehicle-upkeep', name: 'Seat Covers', gstClaimable: true },
    { category: 'vehicle-upkeep', name: 'Vacuum', gstClaimable: true },
  ],
  insurance: [
    { category: 'insurance', name: 'Broker Fees', gstClaimable: true },
    { category: 'insurance', name: 'ICBC Premium', gstClaimable: false },
    {
      category: 'insurance',
      name: 'Service Fees',
      gstClaimable: true,
      description: 'Only service fee GST claimable',
    },
  ],
  'phone-data': [
    { category: 'phone-data', name: 'Data Overage', gstClaimable: true },
    { category: 'phone-data', name: 'Hotspot', gstClaimable: true },
    {
      category: 'phone-data',
      name: 'Mobile Plan',
      gstClaimable: true,
      description: 'Business % configurable',
    },
    { category: 'phone-data', name: 'SIM Card', gstClaimable: true },
  ],
  accessories: [
    { category: 'accessories', name: 'Dash Cam', gstClaimable: true },
    { category: 'accessories', name: 'GPS Device', gstClaimable: true },
    { category: 'accessories', name: 'Phone Charger', gstClaimable: true },
    { category: 'accessories', name: 'Phone Mount', gstClaimable: true },
    { category: 'accessories', name: 'Safety Gear', gstClaimable: true },
  ],
  clothing: [
    { category: 'clothing', name: 'Reflective Vest', gstClaimable: true },
    { category: 'clothing', name: 'Uber Uniform', gstClaimable: true, description: 'If mandated' },
    {
      category: 'clothing',
      name: 'Work Gloves',
      gstClaimable: true,
      description: 'Safety-specific only',
    },
  ],
  meals: [
    { category: 'meals', name: 'Coffee', gstClaimable: false, description: 'Income tax only' },
    { category: 'meals', name: 'Lunch', gstClaimable: false, description: 'Income tax only' },
    { category: 'meals', name: 'Snacks', gstClaimable: false, description: 'Income tax only' },
  ],
  entertainment: [
    {
      category: 'entertainment',
      name: 'Audible',
      gstClaimable: false,
      description: 'Personal use',
    },
    {
      category: 'entertainment',
      name: 'SiriusXM',
      gstClaimable: false,
      description: 'Personal use',
    },
    {
      category: 'entertainment',
      name: 'Spotify',
      gstClaimable: false,
      description: 'Personal use',
    },
  ],
  miscellaneous: [
    { category: 'miscellaneous', name: 'Office Supplies', gstClaimable: true },
    { category: 'miscellaneous', name: 'Parking', gstClaimable: true },
    {
      category: 'miscellaneous',
      name: 'Small Tools',
      gstClaimable: true,
      description: 'If business-related',
    },
    { category: 'miscellaneous', name: 'Tolls', gstClaimable: true },
  ],
  'lease-payment': [
    { category: 'lease-payment', name: 'Lease Admin Fee', gstClaimable: true },
    { category: 'lease-payment', name: 'Monthly Lease Payment', gstClaimable: true },
  ],
  'loan-interest': [
    {
      category: 'loan-interest',
      name: 'Auto Loan Interest',
      gstClaimable: false,
      description: 'Interest has no GST/HST',
    },
  ],
  'tax-services': [
    { category: 'tax-services', name: 'AutoTaxPilot Organizer Fee', gstClaimable: true },
  ],
  custom: [
    {
      category: 'custom',
      name: 'Custom Expense',
      gstClaimable: true,
      description: 'User-defined expense (itemize details in description)',
    },
  ],
};

export interface Province {
  code: string;
  name: string;
  gstRate: number;
  pstRate: number;
  hstRate: number;
}

export const PROVINCES: Province[] = [
  { code: 'BC', name: 'British Columbia', gstRate: 5, pstRate: 7, hstRate: 0 },
  { code: 'AB', name: 'Alberta', gstRate: 5, pstRate: 0, hstRate: 0 },
  { code: 'MB', name: 'Manitoba', gstRate: 5, pstRate: 7, hstRate: 0 },
  { code: 'ON', name: 'Ontario', gstRate: 0, pstRate: 0, hstRate: 13 },
];

export interface DashboardStats {
  earningsThisMonth: number;
  deductiblesCaptured: number;
  gstToRemit: number;
  nextFilingDate: Date;
  totalExpenses: number;
  totalSessions: number;
  averageEarningsPerSession: number;
  itcCaptureRate: number;
}
